import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Profile, Callback, NotFound, Dashboard, ServerPanel, PaymentSuccess, BuyCredits, ServerConsole, Terms, TransferCredits, TransferSuccess, CreateServer, Waitlist } from './pages';
import { Home } from './marketing/pages/Home';
import { VSMonthlyHosting } from './marketing/pages/VSMonthlyHosting';
import { BlogIndex } from './marketing/pages/BlogIndex';
import { BlogPost } from './marketing/pages/BlogPost';
import { Minecraft } from './marketing/pages/Minecraft';
import { CounterStrike } from './marketing/pages/CounterStrike';
import './App.css';

function App() {
  const location = useLocation();
  const scriptLoaded = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout>();

  // Rutas donde el chat debe ser visible
  const showChatwoot = ['/', '/dashboard'].includes(location.pathname);

  useEffect(() => {
    const styleId = 'chatwoot-visibility-override';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .woot-widget-holder,
        .woot--bubble-holder {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const applyVisibility = () => {
    const widgetHolder = document.querySelector('.woot-widget-holder') as HTMLElement | null;
    const bubbleHolder = document.querySelector('.woot--bubble-holder') as HTMLElement | null;

    // Forzar estilo con !important para que no sea sobrescrito
    if (widgetHolder) {
      widgetHolder.style.setProperty('display', showChatwoot ? 'block' : 'none', 'important');
    }
    if (bubbleHolder) {
      bubbleHolder.style.setProperty('display', showChatwoot ? 'block' : 'none', 'important');
    }

    // Intentar también con el SDK de Chatwoot (si está disponible)
    const chatwoot = (window as any).$chatwoot;
    if (chatwoot) {
      try {
        if (showChatwoot && typeof chatwoot.show === 'function') {
          chatwoot.show();
        } else if (!showChatwoot && typeof chatwoot.hide === 'function') {
          chatwoot.hide();
        }
      } catch (e) {
      }
    }
  };

  useEffect(() => {
    if (scriptLoaded.current) return;

    const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL as string;
    const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN as string;

    const script = document.createElement('script');
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      (window as any).chatwootSDK?.run({
        websiteToken: WEBSITE_TOKEN,
        baseUrl: BASE_URL,
      });
    };
    document.body.appendChild(script);
    scriptLoaded.current = true;

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Aplicar inmediatamente por si ya existe
    applyVisibility();

    // Establecer un intervalo que verifique cada 200ms
    intervalRef.current = setInterval(() => {
      applyVisibility();
    }, 200);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [showChatwoot]); // Se reinicia cuando cambia la ruta

  return (
    <Routes>
      {/* Páginas de marketing (SSG pre-renderizadas a HTML estático) */}
      <Route path="/" element={<Home />} />
      <Route path="/vs/hosting-mensual" element={<VSMonthlyHosting />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/minecraft" element={<Minecraft />} />
      <Route path="/counter-strike" element={<CounterStrike />} />

      {/* App autenticada (SPA) */}
      <Route path="/profile" element={<Profile />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/create-server" element={<CreateServer />} />
      <Route path="/server/:id" element={<ServerPanel />} />
      <Route path="/server/:id/console" element={<ServerConsole />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/buy-credits" element={<BuyCredits />} />
      <Route path="/transfer" element={<TransferCredits />} />
      <Route path="/transfer/success" element={<TransferSuccess />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
